import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { Button, Checkbox, CheckboxGroup, Modal, Notification, Table } from 'rsuite';

// Importado do caminho do proprio componente, e nao do indice de components: aquele indice arrasta a
// arvore de rotas inteira e o teste deste modal quebra em importacao circular antes de renderizar.
import { Callout } from '../../../../../../../components/Callout';
import { CostNegotiationMode, DEFAULT_COST_NEGOTIATION_MODE } from '../../../NegociacaoFornecedor/costNegotiation';
import type { NegotiationDataItemProps } from '../../../NegociacaoFornecedor/NegociacaoFornecedor.types';
import { GETDatapointsDetalhesDaNegociacao, POSTResumoAoFornecedorFile } from '../../DetalhesDaNegociacao.services';
import { ColunaDoResumo, COLUNAS_FIXAS, COLUNAS_OPCIONAIS, rotuloDaColuna, useSelecaoDeColunas } from './ResumoDeEnvioModal.hooks';
import styles from './ResumoDeEnvioModal.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

export const TITULO_DO_RESUMO = 'Resumo para o fornecedor';

/**
 * O aviso do envio. O botão existe para dizer que o caminho está previsto; o envio automático não
 * está implementado e é recusa registrada, não dívida esquecida.
 */
export const AVISO_DO_ENVIO = 'O envio direto ao fornecedor chega numa versão futura. Por enquanto, baixe o arquivo e envie você mesmo.';

export const ROTULO_DO_BOTAO_EXCEL = 'Baixar Excel';

/** O que o comprador lê quando o arquivo não sai. Falha silenciosa aqui vira arquivo vazio no fornecedor. */
export const ERRO_AO_GERAR_EXCEL = 'Não foi possível gerar o Excel do resumo. Tente de novo.';

export const TITULO_DO_SELETOR = 'Colunas do Excel';

/**
 * Os títulos das duas seções. Eles são o que diz por que cinco cartões não desligam: a seção se chama
 * SEMPRE NO ARQUIVO, e o cartão acinzentado com a marca ligada mostra o que sai de todo jeito.
 */
export const SECAO_DAS_FIXAS = 'Sempre no arquivo';
export const SECAO_DAS_OPCIONAIS = 'Escolha do comprador';

/** O rodapé do bloco. A escolha some do bloco quando o modal fecha, e o comprador precisa saber que não. */
export const RODAPE_DA_SELECAO = 'Sua seleção fica salva para os próximos envios.';

// Cada celula de valor carrega a identidade da LINHA no proprio identificador de teste: a tabela
// do rsuite desenha cada coluna numa subarvore propria, e nao existe um elemento que contenha a
// linha inteira para ancorar a busca.

/** O que a tabela mostra onde não há dado. Nunca R$ 0,00: zero é um acordo de zero reais. */
const VAZIO = '--';

const TAMANHO_DA_PAGINA = 20;
const ALTURA_DO_CABECALHO = 44;
const ALTURA_DA_LINHA = 56;
const LINHAS_VISIVEIS = 8;

interface ResumoDeEnvioModalProps {
  negotiationId: string;
  // Opcional porque o par de abertura de modal desta tela publica `show` como possivelmente ausente,
  // e ausente significa fechado -- o mesmo que falso.
  show?: boolean;
  onHide: () => void;
  /**
   * O modo de custo em que o cliente negocia, e o que decide o sufixo de tres rotulos de coluna.
   *
   * Vem da grade, que ja o resolveu, e nao de uma segunda consulta a configuracao do cliente.
   * Ausente cai no custo liquido, o mesmo recuo da grade: rotulo sem sufixo nao afirma nada de
   * errado, e "(entrada)" cedo demais afirmaria.
   */
  costNegotiationMode?: CostNegotiationMode;
}

/**
 * O resumo de/para que o comprador manda ao fornecedor: um item por linha, com o custo negociado e a
 * verba nas duas leituras — a do líquido do varejista, que é a que ele digita, e a do custo de
 * entrada, que é a que o fornecedor recebe.
 *
 * <p><strong>Busca uma página por vez.</strong> "Lista todos os itens" quer dizer que nenhum item
 * fica fora da navegação, não que o navegador carregue a negociação inteira — uma negociação deste
 * módulo pode ter milhões de itens.
 *
 * <p><strong>Nenhuma conta acontece aqui:</strong> os dois números da verba chegam prontos do
 * motor, pela mesma rota que a tela de detalhes já usa.
 */
export const ResumoDeEnvioModal = ({ negotiationId, show, onHide, costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE }: ResumoDeEnvioModalProps) => {
  const [itens, setItens] = useState<NegotiationDataItemProps[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const colunas = useSelecaoDeColunas(show);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      // Sem cliente no parâmetro: o inquilino é resolvido pelo token, do lado do gateway.
      const resposta = await GETDatapointsDetalhesDaNegociacao({
        ipaNegotiationId: negotiationId,
        page: pagina,
        size: TAMANHO_DA_PAGINA,
      });
      setItens(resposta?.content ?? []);
      setTotal(resposta?.totalElements ?? 0);
    } finally {
      setCarregando(false);
    }
  }, [negotiationId, pagina]);

  // A seleção do comprador vai como está: `undefined` para quem nunca escolheu, e aí o motor responde
  // com o conjunto padrão; lista vazia para quem desmarcou tudo, e aí saem só as colunas fixas.
  const baixarExcel = useCallback(async () => {
    // O segundo clique não dispara uma segunda geração: o arquivo não sai instantâneo.
    if (gerando) return;
    setGerando(true);
    try {
      await POSTResumoAoFornecedorFile(negotiationId, colunas.paraOPedido);
    } catch {
      Notification.error({ title: ERRO_AO_GERAR_EXCEL, duration: 5000 });
    } finally {
      setGerando(false);
    }
  }, [negotiationId, gerando, colunas.paraOPedido]);

  useEffect(() => {
    // Modal fechado não busca: sem esta guarda o resumo consultaria em toda montagem da tela de
    // detalhes, para uma lista que ninguém abriu.
    if (!show) return;
    buscar();
  }, [show, buscar]);

  // Fechar e reabrir volta para a primeira página: reabrir na página sete deixa o comprador
  // olhando um pedaço do meio sem saber por quê.
  useEffect(() => {
    if (!show) setPagina(0);
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" className={styles['resumo-modal']}>
      <Modal.Header>
        <Modal.Title className={styles['resumo-titulo']}>{TITULO_DO_RESUMO}</Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles['resumo-corpo']}>
        <section className={styles['resumo-colunas']} aria-label={TITULO_DO_SELETOR}>
          <span className={styles['resumo-colunas-titulo']}>{TITULO_DO_SELETOR}</span>

          {/*
            O arquivo é o único artefato desta tela que sai da mesa do varejista e chega à do
            fornecedor. O aviso diz, antes de qualquer caixa, que a língua interna não vai junto.
          */}
          <Callout variant="info" size="sm" data-testid="resumo-aviso-do-arquivo">
            {/*
              O Callout empilha os filhos em coluna, então texto solto quebra numa linha por nó
              do JSX -- o negrito ia sozinho para o meio e o ponto final para a linha seguinte.
              Description é um parágrafo único, e aí o texto flui.
            */}
            <Callout.Description>
              O arquivo fala a língua do fornecedor: custo líquido, PMZ e margens <strong>não entram</strong>. Na
              negociação fechada, os valores são os da fotografia.
            </Callout.Description>
          </Callout>

          <span className={styles['resumo-colunas-secao']}>{SECAO_DAS_FIXAS}</span>

          {/*
            As fixas em grupo próprio, com valor imutável: elas aparecem para o comprador ver o que
            vai no arquivo, e nenhuma interação as desliga. O motor as devolve de todo modo.
          */}
          <CheckboxGroup name="resumo-colunas-fixas" value={COLUNAS_FIXAS.map(coluna => coluna.nome)}>
            {COLUNAS_FIXAS.map(coluna => (
              <div key={coluna.nome} className={classNames(styles['resumo-cartao'], styles['resumo-cartao-fixo'])} data-testid={cartaoDe(coluna)}>
                <Checkbox value={coluna.nome} disabled>
                  <span className={styles['resumo-cartao-rotulo']}>{rotuloDaColuna(coluna, costNegotiationMode)}</span>
                </Checkbox>
              </div>
            ))}
          </CheckboxGroup>

          <span className={styles['resumo-colunas-secao']}>{SECAO_DAS_OPCIONAIS}</span>

          <CheckboxGroup name="resumo-colunas-opcionais" value={colunas.marcadas} onChange={valor => colunas.definir((valor ?? []) as string[])}>
            {COLUNAS_OPCIONAIS.map(coluna => {
              const marcada = colunas.marcadas.includes(coluna.nome);

              return (
                <div
                  key={coluna.nome}
                  className={classNames(styles['resumo-cartao'], marcada && styles['resumo-cartao-marcado'])}
                  data-testid={cartaoDe(coluna)}
                >
                  <Checkbox value={coluna.nome}>
                    <span className={styles['resumo-cartao-rotulo']}>{rotuloDaColuna(coluna, costNegotiationMode)}</span>
                  </Checkbox>
                  {/*
                    A frase é o que separa um cartão de uma caixa de seleção grande à toa: ela diz por
                    que aquela coluna serve, que é a pergunta que o comprador tem diante da caixa.
                  */}
                  <span className={styles['resumo-cartao-frase']}>{coluna.frase}</span>
                </div>
              );
            })}
          </CheckboxGroup>

          <span className={styles['resumo-colunas-rodape']}>{RODAPE_DA_SELECAO}</span>
        </section>

        <Table
          data={itens}
          loading={carregando}
          headerHeight={ALTURA_DO_CABECALHO}
          rowHeight={ALTURA_DA_LINHA}
          height={ALTURA_DO_CABECALHO + ALTURA_DA_LINHA * LINHAS_VISIVEIS}
          rowKey="productsToBePricedId"
        >
          {/* @ts-expect-error coluna não pode receber children */}
          <Column flexGrow={2}>
            <HeaderCell>Produto</HeaderCell>
            <Cell dataKey="productId">
              {(item: NegotiationDataItemProps) => (
                <div data-testid={`resumo-linha-${item.productsToBePricedId}`} className={styles['resumo-produto']}>
                  <span className={styles['resumo-produto-codigo']}>{item.productId}</span>
                  <span className={styles['resumo-produto-descricao']}>{item.description}</span>
                </div>
              )}
            </Cell>
          </Column>

          {/* @ts-expect-error coluna não pode receber children */}
          <Column flexGrow={1}>
            <HeaderCell>Grão</HeaderCell>
            <Cell dataKey="store">{(item: NegotiationDataItemProps) => <span>{rotuloDoGrao(item)}</span>}</Cell>
          </Column>

          {/* @ts-expect-error coluna não pode receber children */}
          <Column flexGrow={1} align="right">
            <HeaderCell>Custo negociado</HeaderCell>
            <Cell dataKey="newCost">
              {(item: NegotiationDataItemProps) => <span data-testid={`resumo-novo-custo-${item.productsToBePricedId}`}>{moeda(item.newCost)}</span>}
            </Cell>
          </Column>

          {/* @ts-expect-error coluna não pode receber children */}
          <Column flexGrow={1} align="right">
            <HeaderCell>Verba no líquido</HeaderCell>
            <Cell dataKey="sellinAllowance">
              {(item: NegotiationDataItemProps) => <span data-testid={`resumo-verba-liquido-${item.productsToBePricedId}`}>{moeda(verbaAcordada(item))}</span>}
            </Cell>
          </Column>

          {/* @ts-expect-error coluna não pode receber children */}
          <Column flexGrow={1} align="right">
            <HeaderCell>Verba a conceder</HeaderCell>
            <Cell dataKey="sellinAllowanceToSupplier">
              {(item: NegotiationDataItemProps) => <span data-testid={`resumo-verba-fornecedor-${item.productsToBePricedId}`}>{moeda(verbaAoFornecedor(item))}</span>}
            </Cell>
          </Column>
        </Table>

        <Pagination
          total={total}
          activePage={pagina + 1}
          displayLength={TAMANHO_DA_PAGINA}
          onChangePage={(proxima: number) => setPagina(proxima - 1)}
        />
      </Modal.Body>

      <Modal.Footer className={styles['resumo-rodape']}>
        <span className={styles['resumo-aviso']}>{AVISO_DO_ENVIO}</span>
        <Button appearance="ghost" onClick={baixarExcel} loading={gerando} data-testid="resumo-baixar-excel">
          {ROTULO_DO_BOTAO_EXCEL}
        </Button>
        {/*
          O botão nasce desabilitado e NÃO recebe manipulador de clique. A ausência do manipulador é a
          garantia: enquanto ele existir, basta alguém habilitar o botão um dia para o envio disparar
          sem que ninguém tenha implementado o envio.
        */}
        <Button appearance="primary" disabled>
          Enviar para o fornecedor
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** O identificador do cartão no teste. O nome interno da coluna é o que não muda quando o rótulo muda. */
const cartaoDe = (coluna: ColunaDoResumo) => `resumo-cartao-${coluna.nome}`;

/**
 * A verba ACORDADA do item, e o único lugar onde esta tela decide o que é vazio.
 *
 * <p>Sem acordo, as duas colunas ficam vazias — mesmo quando o motor devolve um número em
 * `sellinAllowanceToSupplier`, que é a verba VIGENTE convertida e recua para a sugestão quando
 * ninguém acordou nada. Mostrá-la aqui afirmaria ao fornecedor um acordo que não existe.
 */
const verbaAcordada = (item: NegotiationDataItemProps): number | null | undefined =>
  item.sellinAllowance == null ? null : item.sellinAllowance;

const verbaAoFornecedor = (item: NegotiationDataItemProps): number | null | undefined =>
  item.sellinAllowance == null ? null : item.sellinAllowanceToSupplier;

const rotuloDoGrao = (item: NegotiationDataItemProps): string => {
  if (item.visionSaved === 'NETWORK') return 'Toda a rede';
  if (item.visionSaved === 'CLUSTER') return item.cluster?.clusterName ?? VAZIO;
  return item.store ?? VAZIO;
};

const moeda = (valor: number | null | undefined) => {
  if (valor == null) return VAZIO;
  return <CurrencyFormat value={valor} displayType="text" decimalSeparator="," thousandSeparator="." decimalScale={2} fixedDecimalScale prefix="R$ " />;
};
